import { useEffect, useMemo } from 'react'
import { Checkbox, Creatable, Select } from '@iveco-ti/iveco-ds'
import { useFormContext } from '@iveco-ti/iveco-ds/hook-form'
import { useTranslation } from 'react-i18next'

import { JsonInput } from '@/common/components/JsonInput'
import { ApplicationAccountModel } from '@/common/models/applicationAccount'
import { useAllApplications } from '../../../../../common/hooks/useAllApplications'
import { useEditApplicationAccount } from '../hooks/useEditApplicationAccount'
import { useManageApplicationsAccount } from '../hooks/useManageApplicationsAccount'
import { ApplicationAccountForm } from '../schemas/ApplicationAccountSchema'

type FormEditApplicationAccountProps = {
  applicationAccount: ApplicationAccountModel
  onEdit: () => void
}

export function FormEditApplicationAccount({
  applicationAccount,
  onEdit,
}: FormEditApplicationAccountProps) {
  const { applications } = useAllApplications()
  const { t } = useTranslation('sso')
  const { ACCOUNT_SELECTED } = useManageApplicationsAccount()
  const { handleEditApplicationAccount } = useEditApplicationAccount()
  const { reset, handleSubmit: formSubmit } =
    useFormContext<ApplicationAccountForm>()

  const handleSubmit = async (data: ApplicationAccountForm) => {
    if (!applicationAccount) return

    await handleEditApplicationAccount(applicationAccount, data).then(() => {
      onEdit()
    })
  }

  const APPLICATION_OPTIONS = useMemo(
    () =>
      applications.map((application) => ({
        value: application.id,
        label: application.title,
      })),
    [applications],
  )

  useEffect(() => {
    if (applicationAccount) {
      reset({
        accountFields: applicationAccount.accountFields
          ? JSON.stringify(applicationAccount.accountFields)
          : '',
        applicationId: applicationAccount.applicationId,
        tag: applicationAccount.tag.map((tag) => ({ value: tag, label: tag })),
        isMainAccount: applicationAccount.isMainAccount,
      })
    }
  }, [applicationAccount, reset])

  useEffect(() => {
    if (!ACCOUNT_SELECTED || !applicationAccount) reset()
  }, [ACCOUNT_SELECTED, applicationAccount, reset])

  return (
    <form className="space-y-6" onSubmit={formSubmit(handleSubmit)}>
      <Select.Single
        name="applicationId"
        label={t(
          'accounts.drawer_manage_applications.fields.applicationId.label',
        )}
        placeholder={t(
          'accounts.drawer_manage_applications.fields.applicationId.placeholder',
        )}
        disabled
        options={APPLICATION_OPTIONS}
        noOptionsText={t(
          'accounts.drawer_manage_applications.fields.applicationId.noOptionsText',
        )}
      />

      <Creatable.Sync
        isMulti
        icon={null}
        name="tag"
        label={t('accounts.drawer_manage_applications.fields.tag.label')}
        placeholder={t(
          'accounts.drawer_manage_applications.fields.tag.placeholder',
        )}
        formatCreateLabel={(value) =>
          t(
            'accounts.drawer_manage_applications.fields.tag.formatCreateLabel',
            { value },
          )
        }
      />

      <JsonInput
        name="accountFields"
        label={t(
          'accounts.drawer_manage_applications.fields.accountFields.label',
        )}
        placeholder={t(
          'accounts.drawer_manage_applications.fields.accountFields.placeholder',
        )}
      />

      <Checkbox
        name="isMainAccount"
        text={t(
          'accounts.drawer_manage_applications.fields.isMainAccount.label',
        )}
      />
    </form>
  )
}
